import React, { useCallback, useEffect, useState } from 'react';
import { CreditCardIcon, BuildingStorefrontIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import type { BusinessLine, PaymentMethodConfig } from '../types';

type Tab = 'payment' | 'business';

const KIND_LABELS: Record<string, string> = {
  transfer: 'Transferencia',
  card: 'Tarjeta',
  bizum: 'Bizum',
  cash: 'Efectivo',
  paypal: 'PayPal',
  direct_debit: 'Domiciliación'
};

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Facturas manuales',
  reservas: 'Reservas Entik Records',
  woocommerce: 'WooCommerce',
  import: 'Importación documentos'
};

export const PaymentBusinessSettings: React.FC<{ initialTab?: Tab }> = ({ initialTab = 'payment' }) => {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [methods, setMethods] = useState<PaymentMethodConfig[]>([]);
  const [sourceDefaults, setSourceDefaults] = useState<Record<string, string>>({});
  const [lines, setLines] = useState<BusinessLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [pm, bl] = await Promise.all([apiService.getPaymentMethods(), apiService.getBusinessLines()]);
    if (!pm.error && pm.data) {
      setMethods(pm.data.paymentMethods || []);
      setSourceDefaults(pm.data.sourceDefaults || {});
    }
    if (!bl.error && bl.data) setLines(bl.data.businessLines || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSourceDefaults = async () => {
    const res = await apiService.updateSourcePaymentDefaults(sourceDefaults);
    setMessage(res.error ? res.error : 'Defaults por origen guardados');
  };

  const updateMethod = async (m: PaymentMethodConfig, patch: Partial<PaymentMethodConfig>) => {
    const res = await apiService.updatePaymentMethod(m.id, {
      name: patch.name ?? m.name,
      instructionsText: patch.instructionsText ?? m.instructionsText,
      config: patch.config ?? m.config,
      isDefault: patch.isDefault
    });
    if (!res.error) await load();
    else setMessage(res.error);
  };

  const addMethod = async () => {
    const res = await apiService.createPaymentMethod({
      code: `custom_${Date.now()}`,
      name: 'Nueva forma de pago',
      kind: 'transfer',
      config: {},
      instructionsText: ''
    });
    if (!res.error) await load();
  };

  const saveLine = async (line: BusinessLine) => {
    const res = await apiService.updateBusinessLine(line.id, {
      name: line.name,
      categoryLabel: line.categoryLabel,
      pdfFooter: line.pdfFooter,
      defaultPaymentMethodId: line.defaultPaymentMethodId
    });
    setMessage(res.error ? res.error : 'Línea guardada');
    if (!res.error) await load();
  };

  if (loading) return <p className="text-sm text-gray-500">Cargando configuración de facturación…</p>;

  return (
    <div className="space-y-6">
      <div className="flex gap-2 border-b pb-2">
        <button type="button" onClick={() => setTab('payment')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === 'payment' ? 'bg-violet-100 text-violet-800' : 'text-gray-600 hover:bg-gray-50'}`}>
          <CreditCardIcon className="h-4 w-4 inline mr-1" /> Formas de pago
        </button>
        <button type="button" onClick={() => setTab('business')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === 'business' ? 'bg-violet-100 text-violet-800' : 'text-gray-600 hover:bg-gray-50'}`}>
          <BuildingStorefrontIcon className="h-4 w-4 inline mr-1" /> Líneas de negocio
        </button>
      </div>

      {message ? <p className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded">{message}</p> : null}

      {tab === 'payment' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Configure IBAN, Bizum, textos de tarjeta y el método por defecto según el origen de la factura.</p>

          <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-900">Default por origen</h4>
            {Object.entries(SOURCE_LABELS).map(([key, label]) => (
              <div key={key} className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-700 w-48">{label}</span>
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={sourceDefaults[key] || 'transfer'}
                  onChange={(e) => setSourceDefaults((s) => ({ ...s, [key]: e.target.value }))}
                >
                  {methods.map((m) => (
                    <option key={m.id} value={m.kind}>{m.name}</option>
                  ))}
                </select>
              </div>
            ))}
            <button type="button" onClick={saveSourceDefaults} className="text-sm bg-violet-700 text-white px-3 py-1.5 rounded hover:bg-violet-800">Guardar defaults</button>
          </div>

          {methods.map((m) => (
            <div key={m.id} className="border rounded-lg p-4 bg-white space-y-2">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <span className="text-xs text-gray-500">{KIND_LABELS[m.kind] || m.kind}</span>
                  <input className="block w-full font-medium border-b border-transparent focus:border-violet-400 outline-none" value={m.name} onChange={(e) => setMethods((list) => list.map((x) => x.id === m.id ? { ...x, name: e.target.value } : x))} onBlur={() => updateMethod(m, { name: m.name })} />
                </div>
                <label className="text-xs flex items-center gap-1">
                  <input type="radio" name="defaultPm" checked={!!m.isDefault} onChange={() => updateMethod(m, { isDefault: true })} /> Por defecto
                </label>
              </div>
              {m.kind === 'transfer' && (
                <input placeholder="IBAN" className="w-full border rounded px-2 py-1 text-sm" value={m.config?.iban || ''} onChange={(e) => setMethods((list) => list.map((x) => x.id === m.id ? { ...x, config: { ...x.config, iban: e.target.value } } : x))} onBlur={() => updateMethod(m, { config: m.config })} />
              )}
              {m.kind === 'bizum' && (
                <input placeholder="Teléfono Bizum" className="w-full border rounded px-2 py-1 text-sm" value={m.config?.phone || ''} onChange={(e) => setMethods((list) => list.map((x) => x.id === m.id ? { ...x, config: { ...x.config, phone: e.target.value } } : x))} onBlur={() => updateMethod(m, { config: m.config })} />
              )}
              <textarea placeholder="Texto en PDF / email" className="w-full border rounded px-2 py-1 text-sm" rows={2} value={m.instructionsText || ''} onChange={(e) => setMethods((list) => list.map((x) => x.id === m.id ? { ...x, instructionsText: e.target.value } : x))} onBlur={() => updateMethod(m, { instructionsText: m.instructionsText })} />
            </div>
          ))}
          <button type="button" onClick={addMethod} className="flex items-center gap-1 text-sm text-violet-700 hover:text-violet-900"><PlusIcon className="h-4 w-4" /> Añadir forma de pago</button>
        </div>
      )}

      {tab === 'business' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Cada línea define categoría en factura, pie de PDF y forma de pago por defecto (p. ej. Entik Records → tarjeta).</p>
          {lines.map((line) => (
            <div key={line.id} className="border rounded-lg p-4 bg-white space-y-2" style={{ borderLeftColor: line.color || '#6366f1', borderLeftWidth: 4 }}>
              <input className="w-full font-medium border-b outline-none" value={line.name} onChange={(e) => setLines((l) => l.map((x) => x.id === line.id ? { ...x, name: e.target.value } : x))} />
              <input className="w-full text-sm border rounded px-2 py-1" placeholder="Etiqueta categoría" value={line.categoryLabel || ''} onChange={(e) => setLines((l) => l.map((x) => x.id === line.id ? { ...x, categoryLabel: e.target.value } : x))} />
              <textarea className="w-full text-sm border rounded px-2 py-1" rows={2} placeholder="Pie de PDF" value={line.pdfFooter || ''} onChange={(e) => setLines((l) => l.map((x) => x.id === line.id ? { ...x, pdfFooter: e.target.value } : x))} />
              <select className="w-full text-sm border rounded px-2 py-1" value={line.defaultPaymentMethodId || ''} onChange={(e) => setLines((l) => l.map((x) => x.id === line.id ? { ...x, defaultPaymentMethodId: e.target.value || null } : x))}>
                <option value="">— Forma de pago por defecto —</option>
                {methods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button type="button" onClick={() => saveLine(line)} className="text-sm bg-violet-700 text-white px-3 py-1 rounded">Guardar línea</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentBusinessSettings;
